// ClientSummaryReport.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { DateTime } from "luxon";
import DatePicker, { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import ClientBookingFeesSection from "../ClientBookings/DetailsComponents/ClientBookingFeesSection";
import "./clientSummaryReport.css";

applyPlugin(jsPDF);

const VIEW_OPTIONS = [
    { value: "this_month", label: "This Calendar Month" },
    { value: "last_month", label: "Last Calendar Month" },
    { value: "this_week", label: "This Calendar Week" },
    { value: "last_week", label: "Last Calendar Week" },
    { value: "custom", label: "Custom Dates" },
];

const toYMD = (dt) => dt.toFormat("yyyy-LL-dd");
// YYYY-MM-DD string ↔ JS Date, for the react-datepicker range inputs (styled
// to match the booking-request date picker).
// ITEM-8 EXCEPTION (deliberate): these round-trip a locally-PICKED calendar
// day through the datepicker widget — local-midnight in, same local fields
// out — so they are device-safe without a Dubai conversion (which would shift
// the picked day for users west of UTC).
registerLocale("en-GB", enGB);
const ymdToDate = (s) => (s ? DateTime.fromISO(s).toJSDate() : null);
const dateToYMD = (d) => (d ? DateTime.fromJSDate(d).toFormat("yyyy-LL-dd") : "");

function getDefaultRangeForView(viewValue) {
    const nowDubai = DateTime.now().setZone("Asia/Dubai");

    if (viewValue === "this_month") {
        return { startDate: toYMD(nowDubai.startOf("month")), endDate: toYMD(nowDubai.endOf("month")) };
    }

    if (viewValue === "last_month") {
        const last = nowDubai.minus({ months: 1 });
        return { startDate: toYMD(last.startOf("month")), endDate: toYMD(last.endOf("month")) };
    }

    const startOfWeekMon = (dt) => dt.minus({ days: dt.weekday - 1 }).startOf("day");
    const endOfWeekSun = (dt) => startOfWeekMon(dt).plus({ days: 6 }).endOf("day");

    if (viewValue === "this_week") {
        return { startDate: toYMD(startOfWeekMon(nowDubai)), endDate: toYMD(endOfWeekSun(nowDubai)) };
    }

    if (viewValue === "last_week") {
        const lastWeek = nowDubai.minus({ weeks: 1 });
        return { startDate: toYMD(startOfWeekMon(lastWeek)), endDate: toYMD(endOfWeekSun(lastWeek)) };
    }

    return { startDate: "", endDate: "" };
}

const formatMoney = (val) => {
    const n = Number(val ?? 0);
    return `AED ${n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatDubaiDT = (iso) => {
    if (!iso) return "—";
    const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone("Asia/Dubai");
    if (!dt.isValid) return "—";
    return dt.toFormat("dd LLL yyyy, HH:mm");
};

function isYmd(s) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
}

function compareYmd(a, b) {
    // returns -1 if a < b, 0 if equal, 1 if a > b (safe for YYYY-MM-DD)
    if (!a || !b) return 0;
    if (a === b) return 0;
    return a > b ? 1 : -1;
}

/**
 * The brand logo for the PDF, fetched once and kept as a data URL — jsPDF
 * cannot embed from a URL. public/logo.png is coral-on-transparent, 2022x1086.
 */
let _logoDataUrl = null;
async function getLogoDataUrl() {
    if (_logoDataUrl) return _logoDataUrl;
    try {
        const blob = await (await fetch('/logo.png')).blob();
        _logoDataUrl = await new Promise((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result);
            r.readAsDataURL(blob);
        });
    } catch { _logoDataUrl = null; }
    return _logoDataUrl;
}

export default function ClientSummaryReport() {
    const navigate = useNavigate();
    const location = useLocation();

    const [viewOpt, setViewOpt] = useState(VIEW_OPTIONS[0]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const [openBookingIds, setOpenBookingIds] = useState(() => new Set());

    // ✅ properties list comes from a dedicated route (client properties)
    const [clientProperties, setClientProperties] = useState([]);
    const [isLoadingProperties, setIsLoadingProperties] = useState(false);

    // ✅ Draft vs Applied property filters
    const [draftPropertyOpts, setDraftPropertyOpts] = useState([]);
    const [appliedPropertyIds, setAppliedPropertyIds] = useState([]);

    const didInitRef = useRef(false);

    const isCustom = viewOpt?.value === "custom";

    // ----------------------------
    // Load client's properties list
    // ----------------------------
    useEffect(() => {
        const load = async () => {
            setIsLoadingProperties(true);
            try {
                const res = await axios.get("/api/client-requests/properties");
                setClientProperties(Array.isArray(res.data?.properties) ? res.data.properties : []);
            } catch (e) {
                console.error("Failed to load client properties:", e);
                setClientProperties([]);
            } finally {
                setIsLoadingProperties(false);
            }
        };

        load();
    }, []);

    const reportProperties = useMemo(() => (Array.isArray(data?.properties) ? data.properties : []), [data]);

    const propertyOptions = useMemo(() => {
        return clientProperties.map((p) => {
            const building = p.building_name || "—";
            return {
                value: p.property_id,
                label: p.unit_number ? `Unit ${p.unit_number} · ${building}` : building,
            };
        });
    }, [clientProperties]);

    // ✅ Filter report data using APPLIED property IDs only
    const filteredReportProperties = useMemo(() => {
        if (!appliedPropertyIds.length) return reportProperties;
        const allowed = new Set(appliedPropertyIds);
        return reportProperties.filter((p) => allowed.has(p.property_id));
    }, [reportProperties, appliedPropertyIds]);

    // Guest Billed Bookings get their OWN section (2026-08-09): those
    // bookings were invoiced to a one-time guest, never to this client, so
    // they must not sit inside the client's normal (or entity) grouping —
    // for single- AND multi-billing clients alike.
    const guestSplit = useMemo(() => {
        const normal = [];
        const guest = [];
        filteredReportProperties.forEach((p) => {
            const all = p.bookings || [];
            const guestBk = all.filter((b) => b.is_guest_invoice);
            const normalBk = all.filter((b) => !b.is_guest_invoice);
            if (normalBk.length) normal.push({ ...p, bookings: normalBk });
            if (guestBk.length) guest.push({ ...p, bookings: guestBk });
        });
        let bookings = 0, subtotal = 0, vat = 0, total = 0;
        guest.forEach((p) => p.bookings.forEach((b) => {
            bookings += 1;
            subtotal += Number(b.subtotal_amount || 0);
            vat += Number(b.vat_amount || 0);
            total += Number(b.total_with_vat || 0);
        }));
        return { normal, guest, guestTotals: { bookings, subtotal, vat, total } };
    }, [filteredReportProperties]);

    // Headline figures = what is billed to THIS client only (2026-08-09):
    // guest-billed bookings are excluded here and carry their own subtotal
    // inside the Guest Billed Bookings section.
    const bookingsCount = useMemo(() => {
        return guestSplit.normal.reduce((sum, p) => sum + (p.bookings?.length || 0), 0);
    }, [guestSplit.normal]);

    const grandTotals = useMemo(() => {
        let subtotal = 0, vat = 0, total = 0;
        guestSplit.normal.forEach(p => {
            (p.bookings || []).forEach(b => {
                subtotal += Number(b.subtotal_amount || 0);
                vat += Number(b.vat_amount || 0);
                total += Number(b.total_with_vat || 0);
            });
        });
        return { subtotal, vat, total };
    }, [guestSplit.normal]);

    // Multi-billing (PLAN_multi_billing.md Round 2 §3): when the loaded data
    // spans more than one distinct billing entity (null = Default counts as
    // one), the properties render under per-entity section headers with a
    // per-section subtotal — on the summary itself AND in the PDF. A
    // single-entity client (all labels null) gets `null` here and renders
    // byte-identical to before. Guest-billed bookings are excluded — they
    // render in their own section below.
    const entitySections = useMemo(() => {
        const distinct = new Set(
            guestSplit.normal.map((p) => p.property_info?.billing_entity_label || '')
        );
        if (distinct.size <= 1) return null;
        const byEntity = new Map(); // '' = default entity
        guestSplit.normal.forEach((p) => {
            const label = p.property_info?.billing_entity_label || '';
            if (!byEntity.has(label)) byEntity.set(label, []);
            byEntity.get(label).push(p);
        });
        const labels = [...byEntity.keys()].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
        return labels.map((label) => {
            const props = byEntity.get(label);
            let bookings = 0, subtotal = 0, vat = 0, total = 0;
            props.forEach((p) => (p.bookings || []).forEach((b) => {
                bookings += 1;
                subtotal += Number(b.subtotal_amount || 0);
                vat += Number(b.vat_amount || 0);
                total += Number(b.total_with_vat || 0);
            }));
            return { label: label || 'Default Billing', properties: props, bookings, subtotal, vat, total };
        });
    }, [guestSplit.normal]);

    const removeDraftProperty = (propertyId) => {
        setDraftPropertyOpts((prev) => prev.filter((o) => o.value !== propertyId));
    };

    const toggleBooking = (bookingId) => {
        setOpenBookingIds((prev) => {
            const next = new Set(prev);
            if (next.has(bookingId)) next.delete(bookingId);
            else next.add(bookingId);
            return next;
        });
    };

    const expandAll = () => {
        const all = new Set();
        filteredReportProperties.forEach((p) => (p.bookings || []).forEach((b) => all.add(b.booking_id)));
        setOpenBookingIds(all);
    };

    const collapseAll = () => setOpenBookingIds(new Set());

    const [includeLineItems, setIncludeLineItems] = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);

    const generatePdf = async () => {
        if (!filteredReportProperties.length) return;
        setPdfGenerating(true);

        try {
            // If detailed line items requested, fetch all booking fees first
            let feesMap = {};
            if (includeLineItems) {
                const allBookingIds = [];
                filteredReportProperties.forEach(p => (p.bookings || []).forEach(b => allBookingIds.push(b.booking_id)));

                const fetches = allBookingIds.map(id =>
                    axios.get(`/api/client-requests/bookings/${id}/fees`).then(r => ({ id, data: r.data })).catch(() => ({ id, data: null }))
                );
                const results = await Promise.all(fetches);
                results.forEach(r => { if (r.data) feesMap[r.id] = r.data; });
            }

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 14;
            let y = 16;

            // ---- Brand header: logo left, company block right, coral rule ----
            const logo = await getLogoDataUrl();
            // 2022x1086 -> 36mm wide keeps the aspect; the company block is
            // BOTTOM-ALIGNED with the logo so both sit the same distance off
            // the rule — no dead air under the text (2026-08-03).
            const logoTop = y - 4;
            const logoH = 36 * (1086 / 2022);
            const logoBottom = logoTop + logoH;
            if (logo) doc.addImage(logo, 'PNG', margin, logoTop, 36, logoH);
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(40);
            doc.text('Holiday Home Cleaning Services Co. L.L.C', pageWidth - margin, logoBottom - 9, { align: 'right' });
            doc.setFont(undefined, 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(120);
            doc.text('Office 1706-050, Iris Bay Tower, Business Bay, Dubai', pageWidth - margin, logoBottom - 4.5, { align: 'right' });
            doc.text('hello@holidayhomecleaning.ae  ·  holidayhomecleaning.ae', pageWidth - margin, logoBottom, { align: 'right' });
            y = logoBottom + 3;
            doc.setDrawColor(255, 90, 96);
            doc.setLineWidth(0.8);
            doc.line(margin, y, pageWidth - margin, y);
            doc.setLineWidth(0.2);
            y += 8;

            doc.setFontSize(17);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(20);
            doc.text('Bookings Statement', margin, y);
            const dateLabel = startDate && endDate ? `${startDate} to ${endDate}` : viewOpt?.label || '';
            doc.setFontSize(8.5);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(120);
            doc.text(`Period: ${dateLabel}   ·   Generated ${DateTime.now().setZone('Asia/Dubai').toFormat('dd MMM yyyy, HH:mm')}`, pageWidth - margin, y, { align: 'right' });
            y += 7;

            // Summary band — the four numbers a statement is opened for.
            doc.setFillColor(250, 250, 251);
            doc.setDrawColor(233, 233, 236);
            doc.roundedRect(margin, y, pageWidth - margin * 2, 12, 1.5, 1.5, 'FD');
            doc.setFontSize(7);
            doc.setTextColor(130);
            const bandCols = [
                ['BOOKINGS', String(bookingsCount)],
                ['SUBTOTAL (EXCL. VAT)', formatMoney(grandTotals.subtotal)],
                ['VAT (5%)', formatMoney(grandTotals.vat)],
                ['TOTAL (INCL. VAT)', formatMoney(grandTotals.total)],
            ];
            const bandW = (pageWidth - margin * 2) / bandCols.length;
            bandCols.forEach(([label, value], i) => {
                const cx = margin + bandW * i + 4;
                doc.setFont(undefined, 'bold');
                doc.setTextColor(150);
                doc.text(label, cx, y + 4.5);
                doc.setFontSize(9.5);
                doc.setTextColor(i === bandCols.length - 1 ? 40 : 60);
                doc.text(value, cx, y + 9.5);
                doc.setFontSize(7);
            });
            y += 18;
            doc.setTextColor(0);

            // One property block — shared by the flat statement and the
            // per-entity sections below. Mutates the running `y` cursor.
            const renderProp = (prop) => {
                const bookings = prop.bookings || [];
                if (bookings.length === 0) return;

                const propTotal = bookings.reduce((s, b) => s + Number(b.total_with_vat || 0), 0);

                if (y > 260) { doc.addPage(); y = 20; }

                // Property header
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text(prop.property_info?.unit_number ? `Unit ${prop.property_info.unit_number} · ${prop.property_info?.building_name || ''}` : (prop.property_info?.building_name || ''), margin, y);
                doc.setFontSize(9);
                doc.setFont(undefined, 'normal');
                doc.text(`${bookings.length} bookings  |  ${formatMoney(propTotal)}`, pageWidth - margin, y, { align: 'right' });
                y += 5;

                if (!includeLineItems) {
                    // Simple table — no line items, just summary per booking
                    const tableHead = [['Date', 'ID', 'Type', 'Status', 'Subtotal', 'VAT', 'Total']];
                    const tableBody = bookings.map(b => [
                        DateTime.fromISO(b.booking_start, { zone: 'utc' }).setZone('Asia/Dubai').toFormat('dd MMM yy, HH:mm'),
                        `#${b.booking_id}`,
                        `${b.booking_type || '—'}${b.is_guest_invoice ? `\nGuest invoice${b.guest_name ? `: ${b.guest_name}` : ''}` : ''}`,
                        b.booking_status || '—',
                        formatMoney(b.subtotal_amount),
                        formatMoney(b.vat_amount),
                        formatMoney(b.total_with_vat),
                    ]);

                    doc.autoTable({
                        startY: y,
                        head: tableHead,
                        body: tableBody,
                        margin: { left: margin, right: margin },
                        styles: { fontSize: 8, cellPadding: 2.5 },
                        headStyles: { fillColor: [255, 90, 96], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
                        columnStyles: {
                            0: { cellWidth: 35 },
                            1: { cellWidth: 16 },
                            4: { halign: 'right' },
                            5: { halign: 'right' },
                            6: { halign: 'right', fontStyle: 'bold' },
                        },
                        theme: 'grid',
                    });

                    y = doc.lastAutoTable.finalY + 6;
                } else {
                    // Detailed view: each booking as its own table with clear separation
                    for (let bi = 0; bi < bookings.length; bi++) {
                        const b = bookings[bi];
                        const bDate = DateTime.fromISO(b.booking_start, { zone: 'utc' }).setZone('Asia/Dubai').toFormat('dd MMM yy, HH:mm');
                        const fees = feesMap[b.booking_id];

                        if (y > 230) { doc.addPage(); y = 20; }

                        // Booking header bar
                        doc.setFillColor(255, 90, 96);
                        doc.rect(margin, y, pageWidth - margin * 2, 7, 'F');
                        doc.setFontSize(8);
                        doc.setFont(undefined, 'bold');
                        doc.setTextColor(255);
                        doc.text(`#${b.booking_id}   ${bDate}   ${b.booking_type || ''}   ${b.booking_status || ''}${b.is_guest_invoice ? `   GUEST INVOICE${b.guest_name ? ` — ${b.guest_name}` : ''}` : ''}`, margin + 3, y + 5);
                        doc.text(formatMoney(b.total_with_vat), pageWidth - margin - 3, y + 5, { align: 'right' });
                        doc.setTextColor(0);
                        y += 9;

                        // Line items table
                        if (fees && fees.price_items?.length) {
                            const hasDiscount = fees.price_items.some(it => Number(it.discount_percent || 0) > 0);

                            const cols = hasDiscount
                                ? ['Item', 'Qty', 'Unit Price', 'Disc.', 'Subtotal', 'VAT', 'Total']
                                : ['Item', 'Qty', 'Unit Price', 'Subtotal', 'VAT', 'Total'];

                            const rows = fees.price_items.map(item => {
                                const discPct = Number(item.discount_percent || 0);
                                // The client's statement says WHY a price is
                                // not the list price, same as the web view.
                                const overrideNote = item.is_overridden
                                    ? `\n${item.override_percent != null
                                        ? `${String(parseFloat(item.override_percent)).replace(/\.0+$/, '')}% off`
                                        : 'Special price'}${Number(item.list_rate) > 0 ? ` — was ${formatMoney(item.list_rate)}` : ''}`
                                    : '';
                                const row = [
                                    (item.name || item.description || 'Item') + overrideNote,
                                    String(item.quantity || 0).replace(/\.0+$/, ''),
                                    formatMoney(item.unit_price),
                                ];
                                if (hasDiscount) row.push(discPct > 0 ? `${discPct}%` : '—');
                                row.push(formatMoney(Number(item.line_subtotal || 0)));
                                row.push(formatMoney(Number(item.tax_amount || 0)));
                                row.push(formatMoney(Number(item.line_total || 0)));
                                return row;
                            });

                            const lastColIdx = cols.length - 1;
                            const colStyles = {};
                            for (let ci = 2; ci <= lastColIdx; ci++) {
                                colStyles[ci] = { halign: 'right' };
                            }
                            colStyles[lastColIdx] = { halign: 'right', fontStyle: 'bold' };

                            doc.autoTable({
                                startY: y,
                                head: [cols],
                                body: rows,
                                margin: { left: margin, right: margin },
                                styles: { fontSize: 7.5, cellPadding: 2, lineColor: [230, 230, 230], lineWidth: 0.3 },
                                headStyles: { fillColor: [248, 249, 250], textColor: [100, 100, 100], fontStyle: 'bold', fontSize: 7 },
                                columnStyles: colStyles,
                                theme: 'grid',
                            });

                            y = doc.lastAutoTable.finalY;
                        }

                        // Booking totals row
                        doc.setFillColor(248, 249, 250);
                        doc.rect(margin, y, pageWidth - margin * 2, 6, 'F');
                        doc.setDrawColor(230);
                        doc.rect(margin, y, pageWidth - margin * 2, 6, 'S');
                        doc.setFontSize(7);
                        doc.setFont(undefined, 'normal');
                        doc.setTextColor(100);
                        const totalsText = `Subtotal: ${formatMoney(b.subtotal_amount)}     VAT: ${formatMoney(b.vat_amount)}     Total: ${formatMoney(b.total_with_vat)}`;
                        doc.text(totalsText, pageWidth - margin - 3, y + 4.2, { align: 'right' });
                        doc.setTextColor(0);

                        y += 10;
                    }

                    y += 2;
                }
            };

            if (entitySections) {
                // Multi-entity statement (spec Round 2 §3): grouped by billing
                // entity with a per-entity subtotal band; grand total unchanged.
                for (const sec of entitySections) {
                    if (y > 250) { doc.addPage(); y = 20; }
                    doc.setFontSize(11.5);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(255, 90, 96);
                    doc.text(`Billed to: ${sec.label}`, margin, y);
                    doc.setTextColor(0);
                    y += 2;
                    doc.setDrawColor(255, 90, 96);
                    doc.setLineWidth(0.5);
                    doc.line(margin, y, pageWidth - margin, y);
                    doc.setLineWidth(0.2);
                    y += 6;

                    for (const prop of sec.properties) renderProp(prop);

                    // Per-entity subtotal band (mirrors the summary band style).
                    if (y > 255) { doc.addPage(); y = 20; }
                    doc.setFillColor(250, 250, 251);
                    doc.setDrawColor(233, 233, 236);
                    doc.roundedRect(margin, y, pageWidth - margin * 2, 8, 1.5, 1.5, 'FD');
                    doc.setFontSize(8);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(80);
                    doc.text(`${sec.label} — subtotal`, margin + 4, y + 5.2);
                    doc.text(
                        `Subtotal: ${formatMoney(sec.subtotal)}   VAT: ${formatMoney(sec.vat)}   Total: ${formatMoney(sec.total)}`,
                        pageWidth - margin - 4, y + 5.2, { align: 'right' }
                    );
                    doc.setTextColor(0);
                    y += 14;
                }
            } else {
                // Single entity — the statement exactly as it always was
                // (guest-billed bookings excluded; own section below).
                for (const prop of guestSplit.normal) renderProp(prop);
            }

            // Guest Billed Bookings — separate section (2026-08-09):
            // these were invoiced to one-time guests, not to this client.
            if (guestSplit.guest.length > 0) {
                if (y > 250) { doc.addPage(); y = 20; }
                doc.setFontSize(11.5);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(190, 24, 93);
                doc.text('Guest Billed Bookings', margin, y);
                doc.setTextColor(0);
                y += 2;
                doc.setDrawColor(190, 24, 93);
                doc.setLineWidth(0.5);
                doc.line(margin, y, pageWidth - margin, y);
                doc.setLineWidth(0.2);
                y += 4;
                doc.setFontSize(8);
                doc.setFont(undefined, 'normal');
                doc.setTextColor(120);
                doc.text('These bookings were invoiced directly to a guest — they are not billed to your account.', margin, y);
                doc.setTextColor(0);
                y += 6;

                for (const prop of guestSplit.guest) renderProp(prop);

                if (y > 255) { doc.addPage(); y = 20; }
                doc.setFillColor(253, 242, 248);
                doc.setDrawColor(251, 207, 232);
                doc.roundedRect(margin, y, pageWidth - margin * 2, 8, 1.5, 1.5, 'FD');
                doc.setFontSize(8);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(157, 23, 77);
                doc.text('Guest billed — subtotal', margin + 4, y + 5.2);
                doc.text(
                    `Subtotal: ${formatMoney(guestSplit.guestTotals.subtotal)}   VAT: ${formatMoney(guestSplit.guestTotals.vat)}   Total: ${formatMoney(guestSplit.guestTotals.total)}`,
                    pageWidth - margin - 4, y + 5.2, { align: 'right' }
                );
                doc.setTextColor(0);
                y += 14;
            }

            // Grand totals at the bottom
            if (y > 245) { doc.addPage(); y = 20; }

            doc.setDrawColor(200);
            doc.line(margin, y, pageWidth - margin, y);
            y += 7;
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Grand Total', margin, y);
            y += 6;
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            doc.text('Subtotal (excl. VAT):', margin, y);
            doc.text(formatMoney(grandTotals.subtotal), pageWidth - margin, y, { align: 'right' });
            y += 5;
            doc.text('VAT (5%):', margin, y);
            doc.text(formatMoney(grandTotals.vat), pageWidth - margin, y, { align: 'right' });
            y += 5;
            doc.setFont(undefined, 'bold');
            doc.setFontSize(10);
            doc.setTextColor(255, 90, 96);
            doc.text('Total (incl. VAT):', margin, y);
            doc.text(formatMoney(grandTotals.total), pageWidth - margin, y, { align: 'right' });
            doc.setTextColor(0);
            y += 5;
            if (guestSplit.guest.length > 0) {
                doc.setFont(undefined, 'normal');
                doc.setFontSize(8);
                doc.setTextColor(157, 23, 77);
                doc.text('Guest billed (not included above):', margin, y);
                doc.text(formatMoney(guestSplit.guestTotals.total), pageWidth - margin, y, { align: 'right' });
                doc.setTextColor(0);
            }
            y += 5;
            doc.setFontSize(8.5);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(120);
            doc.text('Thank you for choosing Holiday Home Cleaning.', margin, y);

            // Footer on every page: brand line left, page number right.
            const pageCount = doc.internal.getNumberOfPages();
            for (let pi = 1; pi <= pageCount; pi++) {
                doc.setPage(pi);
                const ph = doc.internal.pageSize.getHeight();
                doc.setFontSize(7);
                doc.setTextColor(160);
                doc.text('Holiday Home Cleaning · Dubai · holidayhomecleaning.ae', margin, ph - 8);
                doc.text(`Page ${pi} of ${pageCount}`, pageWidth - margin, ph - 8, { align: 'right' });
            }

            // Save
            const filename = `bookings-statement-${startDate || 'report'}-to-${endDate || 'now'}.pdf`;
            doc.save(filename);
        } catch (err) {
            console.error('Error generating PDF:', err);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setPdfGenerating(false);
        }
    };

    // ✅ Apply-only fetch
    const fetchReport = useCallback(async ({ view, start, end } = {}) => {
        const viewValue = view || "this_month";

        setIsLoading(true);
        setError("");

        try {
            const params = new URLSearchParams();
            params.set("view", viewValue);

            if (viewValue === "custom") {
                if (!start || !end) {
                    setIsLoading(false);
                    setError("Please select both Start Date and End Date.");
                    return;
                }

                if (!isYmd(start) || !isYmd(end)) {
                    setIsLoading(false);
                    setError("Please select valid dates.");
                    return;
                }

                if (compareYmd(start, end) === 1) {
                    setIsLoading(false);
                    setError("End Date must be the same as or after the Start Date.");
                    return;
                }

                params.set("startDate", start);
                params.set("endDate", end);
            }

            const res = await axios.get(`/api/client-requests/bookings/summary-report?${params.toString()}`);
            setData(res.data);

            setOpenBookingIds(new Set());

            const urlParams = new URLSearchParams();
            urlParams.set("view", viewValue);
            if (viewValue === "custom") {
                urlParams.set("startDate", start);
                urlParams.set("endDate", end);
            }
            navigate(`?${urlParams.toString()}`, { replace: true });
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.error || "Failed to load report.");
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    // ---------------------------------------
    // Init from URL (but DO NOT auto-refetch on changes after)
    // ---------------------------------------
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;

        const params = new URLSearchParams(location.search);
        const viewFromUrl = params.get("view");
        const startFromUrl = params.get("startDate");
        const endFromUrl = params.get("endDate");

        const initialView = VIEW_OPTIONS.find((o) => o.value === viewFromUrl) || VIEW_OPTIONS[0];
        setViewOpt(initialView);

        if (initialView.value === "custom") {
            setStartDate(startFromUrl || "");
            setEndDate(endFromUrl || "");
            if (startFromUrl && endFromUrl) {
                fetchReport({ view: "custom", start: startFromUrl, end: endFromUrl });
            }
        } else {
            const r = getDefaultRangeForView(initialView.value);
            setStartDate(r.startDate);
            setEndDate(r.endDate);
            fetchReport({ view: initialView.value });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ when view changes: ONLY update the date inputs. NO fetch.
    useEffect(() => {
        if (!viewOpt?.value) return;

        if (viewOpt.value !== "custom") {
            const r = getDefaultRangeForView(viewOpt.value);
            setStartDate(r.startDate);
            setEndDate(r.endDate);
        }
    }, [viewOpt?.value]);

    const handleApply = () => {
        if (!viewOpt?.value) return;

        // ✅ commit draft properties -> applied properties ONLY on Apply
        const ids = draftPropertyOpts.map((o) => o.value);
        setAppliedPropertyIds(ids);

        if (viewOpt.value === "custom") {
            fetchReport({ view: "custom", start: startDate, end: endDate });
        } else {
            fetchReport({ view: viewOpt.value });
        }
    };

    const openBookingPage = (bookingId) => {
        navigate(`/client/bookings/${bookingId}`);
    };

    const handlePropertiesChange = (vals) => {
        const next = Array.isArray(vals) ? vals : [];
        setDraftPropertyOpts(next);
        // ✅ no refetch and no filtering until Apply
    };

    const resetProperties = () => {
        setDraftPropertyOpts([]);
        // ✅ still no effect until Apply (by design)
    };

    return (
        <div className="client-sum">
            <div className="client-sum__header">
                <div className="client-sum__headerTop">
                    <div className="client-sum__headerLeft">
                        <h1 className="client-sum__title">Bookings Summary Report</h1>
                        <div className="client-sum__sub">
                            Prices shown are estimates based on our system. Final invoice amounts may vary.
                        </div>
                    </div>
                </div>

                <div className="client-sum__filtersRow">
                    <div className="client-sum__filter client-sum__filter--view">
                        <label className="client-sum__label">Report View</label>
                        <Select
                            classNamePrefix="client-select"
                            value={viewOpt}
                            onChange={setViewOpt}
                            options={VIEW_OPTIONS}
                            isSearchable={false}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                        />
                    </div>

                    <div className="client-sum__filter client-sum__filter--properties">
                        <div className="client-sum__labelRow">
                            <label className="client-sum__label">Properties</label>
                            <button
                                type="button"
                                className="client-sum__miniLink"
                                onClick={resetProperties}
                                disabled={!draftPropertyOpts.length}
                                title="Reset selection (then press Apply)"
                            >
                                All
                            </button>
                        </div>

                        <Select
                            classNamePrefix="client-select"
                            value={draftPropertyOpts}
                            onChange={handlePropertiesChange}
                            options={propertyOptions}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions
                            isClearable
                            isSearchable
                            backspaceRemovesValue
                            controlShouldRenderValue={false}
                            placeholder={
                                isLoadingProperties
                                    ? "Loading properties…"
                                    : draftPropertyOpts.length
                                        ? `${draftPropertyOpts.length} Properties Selected`
                                        : "All properties"
                            }
                            isDisabled={isLoading || isLoadingProperties}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                        />
                    </div>

                    <div className="client-sum__filter client-sum__filter--start">
                        <label className="client-sum__label">Start Date</label>
                        <DatePicker
                            selected={ymdToDate(startDate)}
                            onChange={(d) => setStartDate(dateToYMD(d))}
                            maxDate={ymdToDate(endDate) || undefined}
                            dateFormat="EEE d MMM yyyy"
                            locale="en-GB"
                            placeholderText="Select date"
                            disabled={!isCustom}
                            className="client-sum__input"
                            calendarClassName="creq-datepicker-cal"
                            popperClassName="creq-datepicker-popper"
                            popperProps={{ strategy: "fixed" }}
                        />
                    </div>

                    <div className="client-sum__filter client-sum__filter--end">
                        <label className="client-sum__label">End Date</label>
                        <DatePicker
                            selected={ymdToDate(endDate)}
                            onChange={(d) => setEndDate(dateToYMD(d))}
                            minDate={ymdToDate(startDate) || undefined}
                            dateFormat="EEE d MMM yyyy"
                            locale="en-GB"
                            placeholderText="Select date"
                            disabled={!isCustom}
                            className="client-sum__input"
                            calendarClassName="creq-datepicker-cal"
                            popperClassName="creq-datepicker-popper"
                            popperProps={{ strategy: "fixed" }}
                        />
                    </div>

                    <div className="client-sum__filter client-sum__filter--apply">
                        <label className="client-sum__label client-sum__label--ghost">Apply</label>
                        <button
                            className="client-sum__btn client-sum__btnPrimary"
                            onClick={handleApply}
                            disabled={isLoading || (isCustom && (!startDate || !endDate))}
                            title={isCustom && (!startDate || !endDate) ? "Select both dates first" : "Apply"}
                        >
                            Apply
                        </button>
                    </div>

                    {draftPropertyOpts.length ? (
                        <div className="client-sum__selectedPropsRow">
                            <div className="client-sum__selectedPropsLeft">
                                <span className="client-sum__label">
                                    Selected Properties ({draftPropertyOpts.length}):
                                </span>
                            </div>

                            <div className="client-sum__selectedPropsPills">
                                {draftPropertyOpts.map((o) => (
                                    <button
                                        key={o.value}
                                        type="button"
                                        className="client-sum__selectedPropPill"
                                        onClick={() => removeDraftProperty(o.value)}
                                        title="Remove"
                                    >
                                        <span className="client-sum__selectedPropText">{o.label}</span>
                                        <span className="client-sum__selectedPropX">×</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}



                </div>
            </div>

            {isLoading ? (
                <div className="client-sum__inlineLoading" aria-live="polite">
                    <div className="app-loading">
                        <div className="app-spinner" />
                        <div className="app-spinner-text">Loading summary…</div>
                    </div>
                </div>
            ) : null}

            {error ? <div className="client-sum__error">{error}</div> : null}

            {!isLoading && data ? (
                <div className="client-sum__stats">
                    <div className="client-sum__statCards">
                        <div className="client-sum__statCard">
                            <div className="client-sum__statLabel">Bookings</div>
                            <div className="client-sum__statValue">{bookingsCount}</div>
                        </div>
                        <div className="client-sum__statCard">
                            <div className="client-sum__statLabel">Subtotal</div>
                            <div className="client-sum__statValue">{formatMoney(grandTotals.subtotal)}</div>
                        </div>
                        <div className="client-sum__statCard">
                            <div className="client-sum__statLabel">VAT (5%)</div>
                            <div className="client-sum__statValue">{formatMoney(grandTotals.vat)}</div>
                        </div>
                        <div className="client-sum__statCard">
                            <div className="client-sum__statLabel">Total (incl. VAT)</div>
                            <div className="client-sum__statValue">{formatMoney(grandTotals.total)}</div>
                        </div>
                    </div>

                    <div className="client-sum__expandActions">
                        <button className="client-sum__btn client-sum__btnGhost" onClick={expandAll} disabled={isLoading || !data}>
                            Expand all
                        </button>
                        <button className="client-sum__btn client-sum__btnGhost" onClick={collapseAll} disabled={isLoading || !data}>
                            Collapse all
                        </button>

                        <div className="client-sum__pdfGroup">
                            <label className="client-sum__checkLabel">
                                <input
                                    type="checkbox"
                                    checked={includeLineItems}
                                    onChange={(e) => setIncludeLineItems(e.target.checked)}
                                />
                                <span>Include line items</span>
                            </label>
                            <button
                                className="client-sum__btn client-sum__btnPrimary"
                                onClick={generatePdf}
                                disabled={pdfGenerating || isLoading || !data || filteredReportProperties.length === 0}
                            >
                                {pdfGenerating ? 'Generating…' : 'Save as PDF'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {!isLoading && !error && filteredReportProperties.length === 0 ? (
                <div className="client-sum__empty">
                    <div className="client-sum__emptyIcon">🗓️</div>
                    <div className="client-sum__emptyTitle">No bookings found</div>
                    <div className="client-sum__emptyText">
                        {appliedPropertyIds.length
                            ? "Try selecting different dates, or clear the property filter and press Apply."
                            : "Try selecting a different period, or choose Custom Dates and press Apply."}
                    </div>
                </div>
            ) : null}

            {!isLoading && filteredReportProperties.length > 0 ? (
                <div className="client-sum__content">
                    {([
                        // Normal (client-billed) bookings — flat or per-entity.
                        ...(entitySections
                            ? entitySections
                            : [{ label: null, properties: guestSplit.normal }]),
                        // Guest Billed Bookings — always their own section
                        // (2026-08-09), single- and multi-billing alike.
                        ...(guestSplit.guest.length > 0 ? [{
                            label: 'Guest Billed Bookings',
                            isGuest: true,
                            properties: guestSplit.guest,
                            bookings: guestSplit.guestTotals.bookings,
                            subtotal: guestSplit.guestTotals.subtotal,
                            vat: guestSplit.guestTotals.vat,
                            total: guestSplit.guestTotals.total,
                        }] : []),
                    ]).map((sec) => {
                        const cards = sec.properties.map((p) => {
                        const bookings = p.bookings || [];
                        const propTotalInclVat = bookings.reduce((sum, b) => sum + Number(b.total_with_vat || 0), 0);

                        return (
                            <div key={p.property_id} className="client-sum__propertyCard">
                                <div className="client-sum__propertyTop">
                                    <div className="client-sum__propertyTitle">
                                        <div className="client-sum__building">{p.property_info?.unit_number ? `Unit ${p.property_info.unit_number}` : (p.property_info?.building_name || "—")}</div>
                                        {p.property_info?.unit_number ? <div className="client-sum__unit">{p.property_info?.building_name || "—"}</div> : null}
                                    </div>

                                    <div className="client-sum__propertyMeta">
                                        <div className="client-sum__metaPill">{bookings.length} bookings</div>
                                        <div className="client-sum__metaPill client-sum__metaPillStrong">{formatMoney(propTotalInclVat)}</div>
                                    </div>
                                </div>

                                <ul className="client-sum__bookings">
                                    {bookings.map((b) => {
                                        const isOpen = openBookingIds.has(b.booking_id);

                                        return (
                                            <li key={b.booking_id} className={`client-sum__bookingWrap ${isOpen ? "is-open" : ""}`}>
                                                <div
                                                    className="client-sum__bookingRow"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => toggleBooking(b.booking_id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") toggleBooking(b.booking_id);
                                                    }}
                                                    title="Click to expand"
                                                >
                                                    <div className="client-sum__cell client-sum__cellLeft">
                                                        <div className="client-sum__bookingTime">{formatDubaiDT(b.booking_start)}</div>
                                                        <div className="client-sum__bookingId">Booking ID: #{String(b.booking_id)}</div>
                                                    </div>

                                                    <div className="client-sum__cell client-sum__cellMid">
                                                        <div className="client-sum__typeRow">
                                                            <div className="client-sum__type">{b.booking_type || "—"}</div>
                                                            <span className="client-sum__statusChip">{b.booking_status || "—"}</span>
                                                            {/* Guest-invoiced: billed to a one-time guest, not to
                                                                you — so this booking never shows in YOUR invoices
                                                                (2026-08-09). */}
                                                            {b.is_guest_invoice && (
                                                                <span
                                                                    className="client-sum__guestChip"
                                                                    title="This booking was invoiced to a guest, not to your account"
                                                                >
                                                                    Guest invoice{b.guest_name ? ` · ${b.guest_name}` : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="client-sum__cell client-sum__cellRight">
                                                        <div className="client-sum__rowActions">
                                                            <button
                                                                className="client-sum__btn client-sum__btnGhost"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openBookingPage(b.booking_id);
                                                                }}
                                                            >
                                                                View Booking
                                                            </button>
                                                        </div>

                                                        <div className="client-sum__price">{formatMoney(b.total_with_vat)}</div>
                                                        <div className="client-sum__chev" aria-hidden="true">
                                                            {isOpen ? "▲" : "▼"}
                                                        </div>
                                                    </div>
                                                </div>

                                                {isOpen ? (
                                                    <div className="client-sum__bookingDetails">
                                                        <ClientBookingFeesSection
                                                            key={`fees-${b.booking_id}`}
                                                            bookingId={b.booking_id}
                                                            compact={false}
                                                            className="client-sum__feesEmbed"
                                                            initialTotals={{
                                                                subtotal_amount: b.subtotal_amount,
                                                                vat_amount: b.vat_amount,
                                                                total_with_vat: b.total_with_vat,
                                                            }}
                                                        />
                                                    </div>
                                                ) : null}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                        });

                        // Single-entity data → no section wrapper at all,
                        // exactly the markup this page always rendered.
                        if (sec.label === null) return cards;

                        return (
                            <div key={sec.label} className={`client-sum__entitySection${sec.isGuest ? ' client-sum__entitySection--guest' : ''}`}>
                                <div className="client-sum__entityHead">
                                    <span className="client-sum__entityName">{sec.label}</span>
                                    <span className="client-sum__entityCount">
                                        {sec.bookings} booking{sec.bookings === 1 ? "" : "s"}
                                    </span>
                                </div>
                                {sec.isGuest && (
                                    <div className="client-sum__guestSectionNote">
                                        These bookings were invoiced directly to a guest — they are not billed to your account.
                                    </div>
                                )}
                                {cards}
                                <div className="client-sum__entitySubtotal">
                                    <span className="client-sum__entitySubtotalLabel">{sec.isGuest ? 'Guest billed — subtotal' : `${sec.label} — subtotal`}</span>
                                    <span className="client-sum__entitySubtotalFigures">
                                        <span>Subtotal {formatMoney(sec.subtotal)}</span>
                                        <span>VAT {formatMoney(sec.vat)}</span>
                                        <strong>Total {formatMoney(sec.total)}</strong>
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}